/**
 * src/views/LedgerView.tsx
 * Master Ledger View featuring live search, date grouping, and filtered summary totals
 */

import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  ArrowLeftRight, 
  TrendingUp, 
  TrendingDown, 
  RefreshCw, 
  FileText,
  Split,
  Printer,
  Download,
  CreditCard,
  Loader2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { haptic, playTapSound, playSuccessSound } from '../utils/audioHaptics';
import { useLedger } from '../hooks/useLedger';
import { useUsers } from '../hooks/useUsers';
import { api, type CategoryItem, type TransactionItem } from '../services/api';
import { TransactionFilterBar } from '../components/ledger/TransactionFilterBar';
import { TransactionRow } from '../components/ledger/TransactionRow';
import { AdminEntryModal } from '../components/ledger/AdminEntryModal';
import { UserSubmissionModal } from '../components/approvals/UserSubmissionModal';
import { SplitBillModal } from '../components/ledger/SplitBillModal';
import { AppleCardStatementModal } from '../components/statement/AppleCardStatementModal';
import { exportLedgerToCsv } from '../utils/exportStatement';
import { GlassCard } from '../components/ui/GlassCard';
import { GlassButton } from '../components/ui/GlassButton';

export const LedgerView: React.FC = () => {
  const { user } = useAuth();
  const { success, error } = useToast();
  const isAdmin = user?.role === 'admin';

  const { users } = useUsers();
  const [categories, setCategories] = useState<CategoryItem[]>([]);

  // Local filter states
  const [search, setSearch] = useState('');
  const [type, setType] = useState<'paid' | 'receive' | ''>('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Modals state
  const [isEntryModalOpen, setIsEntryModalOpen] = useState(false);
  const [isUserSubmissionOpen, setIsUserSubmissionOpen] = useState(false);
  const [isSplitModalOpen, setIsSplitModalOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [isExportingCsv, setIsExportingCsv] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<TransactionItem | null>(null);

  // Hook for transactions
  const {
    transactions,
    summary,
    totalCount,
    hasMore,
    isLoading,
    isLoadingMore,
    loadMore,
    updateFilters,
    refetch,
    addTransaction,
    editTransaction,
    deleteTransaction
  } = useLedger();

  // Load categories on mount
  useEffect(() => {
    api.categories.list()
      .then(res => setCategories(res.categories || []))
      .catch(() => {});
  }, []);

  // Update query filters when local filter states change
  useEffect(() => {
    updateFilters({
      search,
      type,
      user_id: selectedUser || undefined,
      category_id: selectedCategory || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined
    });
  }, [search, type, selectedUser, selectedCategory, startDate, endDate]);

  const handleResetFilters = () => {
    setSearch('');
    setType('');
    setSelectedUser('');
    setSelectedCategory('');
    setStartDate('');
    setEndDate('');
  };

  const handleCreateOrEditSubmit = async (data: any) => {
    if (editingTransaction) {
      await editTransaction(editingTransaction.id, data);
      success('Transaction updated and user balance recalculated!', 'Updated');
    } else {
      await addTransaction(data);
      success('Transaction recorded into ledger!', 'Created');
    }
    setEditingTransaction(null);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteTransaction(id);
      success('Transaction removed and user balance reverted.', 'Deleted');
    } catch (err: any) {
      error(err.message || 'Failed to delete transaction');
    }
  };

  const handleOpenEdit = (tx: TransactionItem) => {
    setEditingTransaction(tx);
    setIsEntryModalOpen(true);
  };

  // Export complete ledger matching active filters (up to 10,000 records)
  const handleExportFullCsv = async () => {
    try {
      setIsExportingCsv(true);
      haptic.tap();
      playTapSound();

      const res = await api.transactions.list({
        search: search || undefined,
        type: type || undefined,
        user_id: selectedUser || undefined,
        category_id: selectedCategory || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        limit: 10000,
        offset: 0
      });

      const allMatching = res.transactions || [];
      if (allMatching.length === 0) {
        error('No transactions match the criteria to export.');
        return;
      }

      const filename = `CKAudit_Ledger_${new Date().toISOString().slice(0, 10)}.csv`;
      exportLedgerToCsv(allMatching, filename);
      haptic.success();
      playSuccessSound();
      success(`Exported complete ledger (${allMatching.length.toLocaleString('en-IN')} records) to CSV!`, 'Export Complete');
    } catch (err: any) {
      error(err.message || 'Failed to export full CSV');
    } finally {
      setIsExportingCsv(false);
    }
  };

  // Group transactions by date string (YYYY-MM-DD)
  const groupedTransactions: Record<string, TransactionItem[]> = {};
  transactions.forEach(tx => {
    const dateKey = tx.transaction_date ? tx.transaction_date.slice(0, 10) : 'Other';
    if (!groupedTransactions[dateKey]) {
      groupedTransactions[dateKey] = [];
    }
    groupedTransactions[dateKey].push(tx);
  });

  const formatDateHeader = (dateStr: string) => {
    const today = new Date().toISOString().slice(0, 10);
    const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

    if (dateStr === today) return 'Today';
    if (dateStr === yesterday) return 'Yesterday';

    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    return dateStr;
  };

  const isFiltered = Boolean(search || type || selectedUser || selectedCategory || startDate || endDate);
  const displayReceived = summary.totalReceived ?? 0;
  const displayPaid = summary.totalPaid ?? 0;
  const displayNet = summary.netDifference ?? 0;
  const displayCount = totalCount ?? 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header Bar */}
      <div className="flex-between" style={{ flexWrap: 'wrap', gap: '14px' }}>
        <div>
          <h1 style={{ fontSize: '1.8rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ArrowLeftRight size={26} color="var(--indigo-light)" />
            <span>Transaction Ledger</span>
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginTop: '4px' }}>
            {isAdmin 
              ? `Auditing all records (${displayCount.toLocaleString('en-IN')} total entries in database)` 
              : `Your personal credit/debit statement (${displayCount} records)`}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <GlassButton
            variant="ghost"
            icon={<RefreshCw size={15} />}
            onClick={refetch}
            title="Refresh Ledger"
          >
            Refresh
          </GlassButton>

          <GlassButton
            variant="default"
            icon={isExportingCsv ? <Loader2 size={15} className="spin" /> : <Download size={15} />}
            onClick={handleExportFullCsv}
            disabled={isExportingCsv}
            title="Export all matching records to CSV"
          >
            {isExportingCsv ? 'Exporting...' : 'CSV'}
          </GlassButton>

          <GlassButton
            variant="default"
            icon={<CreditCard size={15} />}
            onClick={() => setIsPrintModalOpen(true)}
            title="CKAudit Titanium Statement & Vector PDF"
          >
            Statement (PDF)
          </GlassButton>

          <GlassButton
            variant="default"
            icon={<Split size={15} />}
            onClick={() => setIsSplitModalOpen(true)}
            title="Split Bill across members"
          >
            Split Bill
          </GlassButton>

          {isAdmin ? (
            <GlassButton
              variant="primary"
              icon={<Plus size={16} />}
              onClick={() => {
                setEditingTransaction(null);
                setIsEntryModalOpen(true);
              }}
            >
              Add Entry
            </GlassButton>
          ) : (
            <GlassButton
              variant="primary"
              icon={<Plus size={16} />}
              onClick={() => setIsUserSubmissionOpen(true)}
            >
              Submit Entry
            </GlassButton>
          )}
        </div>
      </div>

      {/* Filtered Financial Summary Banner - Apple Inset Metrics */}
      <div className="grid-cols-3" style={{ gap: '12px' }}>
        {/* Total Received in View */}
        <div className="ios-inset-group" style={{ padding: '14px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
            <TrendingUp size={14} color="var(--ios-green)" />
            <span>Total Received</span>
          </div>
          <div className="num-font" style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--ios-green)', marginTop: '4px', letterSpacing: '-0.02em' }}>
            +₹{displayReceived.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>

        {/* Total Paid in View */}
        <div className="ios-inset-group" style={{ padding: '14px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
            <TrendingDown size={14} color="var(--ios-red)" />
            <span>Total Paid</span>
          </div>
          <div className="num-font" style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: '4px', letterSpacing: '-0.02em' }}>
            -₹{displayPaid.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>

        {/* Net in View */}
        <div className="ios-inset-group" style={{ padding: '14px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
            <ArrowLeftRight size={14} color="var(--ios-blue)" />
            <span>Net Delta</span>
          </div>
          <div className="num-font" style={{ fontSize: '1.25rem', fontWeight: 700, color: displayNet >= 0 ? 'var(--ios-green)' : 'var(--ios-red)', marginTop: '4px', letterSpacing: '-0.02em' }}>
            {displayNet >= 0 ? '+' : '-'}₹{Math.abs(displayNet).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <TransactionFilterBar
        search={search}
        onSearchChange={setSearch}
        selectedType={type}
        onTypeChange={setType}
        selectedUser={selectedUser}
        onUserChange={setSelectedUser}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        startDate={startDate}
        onStartDateChange={setStartDate}
        endDate={endDate}
        onEndDateChange={setEndDate}
        users={users}
        categories={categories}
        isAdmin={isAdmin}
        onReset={handleResetFilters}
      />

      {/* Ledger Feed / Grouped Sections */}
      {isLoading && transactions.length === 0 ? (
        <div className="ios-inset-group flex-center" style={{ padding: '60px', color: 'var(--text-secondary)' }}>
          <RefreshCw size={22} className="animate-spin" style={{ animation: 'spin 1s linear infinite', marginRight: '10px', color: 'var(--ios-blue)' }} />
          <span>Loading ledger transactions...</span>
        </div>
      ) : transactions.length === 0 ? (
        <div className="ios-inset-group flex-center" style={{ padding: '60px', flexDirection: 'column', gap: '12px', textAlign: 'center' }}>
          <FileText size={38} color="var(--text-tertiary)" />
          <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text-primary)' }}>No Transactions Found</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', maxWidth: '340px' }}>
            No entries match your current search filters. Try clearing your filters or adding a new transaction.
          </p>
          {(search || type || selectedUser || selectedCategory) && (
            <GlassButton variant="primary" onClick={handleResetFilters} style={{ marginTop: '8px' }}>
              Clear All Filters
            </GlassButton>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          {Object.entries(groupedTransactions).map(([dateKey, dayTxs]) => {
            const dayNet = Math.round(dayTxs.reduce((sum, tx) => sum + (tx.transaction_type === 'receive' ? tx.amount : -tx.amount), 0) * 100) / 100;
            const dayClosingBalance = dayTxs[0]?.running_balance;

            return (
              <div key={dateKey}>
                {/* iOS Section Header with Date, Day Net and Closing Balance */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px 6px',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {formatDateHeader(dateKey)}
                    </span>
                    <span style={{
                      fontSize: '0.68rem',
                      color: 'var(--text-tertiary)',
                      fontWeight: 600,
                      background: 'rgba(118, 118, 128, 0.15)',
                      padding: '2px 7px',
                      borderRadius: 'var(--radius-pill)'
                    }}>
                      {dayTxs.length} {dayTxs.length === 1 ? 'entry' : 'entries'}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    {/* Day Net Flow */}
                    <span
                      className="num-font"
                      style={{
                        fontSize: '0.74rem',
                        fontWeight: 600,
                        color: dayNet >= 0 ? 'var(--ios-green)' : 'var(--ios-red)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '3px'
                      }}
                      title="Net transaction sum for this day"
                    >
                      <span style={{ color: 'var(--text-tertiary)', fontWeight: 500, fontSize: '0.7rem' }}>Day Net:</span>
                      <span>{dayNet >= 0 ? '+' : '-'}₹{Math.abs(dayNet).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </span>

                    {/* Closing Balance Badge for this Date */}
                    {dayClosingBalance !== undefined && (
                      <span
                        className="num-font"
                        style={{
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          padding: '2px 10px',
                          borderRadius: 'var(--radius-pill)',
                          background: dayClosingBalance > 0
                            ? 'rgba(48, 209, 88, 0.14)'
                            : dayClosingBalance < 0
                              ? 'rgba(255, 69, 58, 0.14)'
                              : 'rgba(100, 210, 255, 0.14)',
                          border: `1px solid ${
                            dayClosingBalance > 0
                              ? 'rgba(48, 209, 88, 0.3)'
                              : dayClosingBalance < 0
                                ? 'rgba(255, 69, 58, 0.3)'
                                : 'rgba(100, 210, 255, 0.3)'
                          }`,
                          color: dayClosingBalance > 0
                            ? 'var(--ios-green)'
                            : dayClosingBalance < 0
                              ? 'var(--ios-red)'
                              : 'var(--ios-teal)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        title="Account closing balance at end of this day"
                      >
                        <span style={{ fontSize: '0.66rem', fontWeight: 600, opacity: 0.85, textTransform: 'uppercase' }}>Closing:</span>
                        <span>{dayClosingBalance >= 0 ? '+' : '-'}₹{Math.abs(dayClosingBalance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Day's Inset Grouped Table */}
                <div className="ios-inset-group">
                  {dayTxs.map(tx => (
                    <TransactionRow
                      key={tx.id}
                      transaction={tx}
                      isAdmin={isAdmin}
                      onEdit={handleOpenEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {/* Pagination & Load More */}
          {hasMore && (
            <div style={{ textAlign: 'center', padding: '16px 0 8px' }}>
              <button
                type="button"
                onClick={loadMore}
                disabled={isLoadingMore}
                className="ios-tap-target"
                style={{
                  padding: '12px 24px',
                  borderRadius: 'var(--radius-pill)',
                  background: 'var(--surface-glass)',
                  border: 'var(--border-hairline)',
                  color: 'var(--text-primary)',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: isLoadingMore ? 'wait' : 'pointer',
                  boxShadow: 'var(--shadow-card)',
                  backdropFilter: 'blur(20px)'
                }}
              >
                {isLoadingMore ? (
                  <>
                    <RefreshCw size={15} className="spin" style={{ animation: 'spin 1s linear infinite' }} />
                    <span>Loading next 50 entries...</span>
                  </>
                ) : (
                  <>
                    <TrendingDown size={15} color="var(--ios-blue)" />
                    <span>Load More (Showing {transactions.length} of {displayCount.toLocaleString('en-IN')})</span>
                  </>
                )}
              </button>
            </div>
          )}

          {!hasMore && transactions.length > 50 && (
            <div style={{ textAlign: 'center', padding: '16px', color: 'var(--text-tertiary)', fontSize: '0.78rem', fontWeight: 500 }}>
              ✓ All {transactions.length.toLocaleString('en-IN')} transactions loaded from database
            </div>
          )}
        </div>
      )}

      {/* Admin Entry Modal (Create & Edit) */}
      {isAdmin && (
        <AdminEntryModal
          isOpen={isEntryModalOpen}
          onClose={() => {
            setIsEntryModalOpen(false);
            setEditingTransaction(null);
          }}
          onSubmit={handleCreateOrEditSubmit}
          users={users}
          categories={categories}
          editTransaction={editingTransaction}
        />
      )}

      {/* User Submission Modal (Awaiting Approval) */}
      {!isAdmin && (
        <UserSubmissionModal
          isOpen={isUserSubmissionOpen}
          onClose={() => setIsUserSubmissionOpen(false)}
          onSubmit={async (data) => {
            try {
              await api.approvals.create(data);
              success('Entry submitted for Admin approval!', 'Request Sent');
              setIsUserSubmissionOpen(false);
            } catch (err: any) {
              error(err.message || 'Failed to submit entry');
              throw err;
            }
          }}
          categories={categories}
        />
      )}

      {/* Split Expenses Modal */}
      <SplitBillModal
        isOpen={isSplitModalOpen}
        onClose={() => setIsSplitModalOpen(false)}
        users={users}
        categories={categories}
        onComplete={refetch}
      />

      {/* Printable Apple Card Statement Modal */}
      <AppleCardStatementModal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        transactions={transactions}
        summary={summary}
        currentUser={user}
      />
    </div>
  );
};

